// src/encounter/wrap-up-pending-order-row.tsx
// One pending order in the wrap-up Pending orders list (PROD-2594). Mirrors pm-react
// `invoice-pending-order-row.tsx` (PROD-2591 + PROD-2630) in behaviour and copy, drawn in the
// wrap-up invoice panel's own table style (the DS `WrapUpInvoicePanel` TH/TD recipe) with Signal
// DS atoms. A vaccine with an administration line is one row that adds two invoice lines.
//
// One practitioner difference, on purpose: a NoPrice order is not a dead end here. Its Add opens
// the existing wrap-up add-service picker, pre-filtered by the order's code, and the picked
// service rides on the add as `serviceId` (the contract allows any service for NoPrice). pm-react
// sends the biller to the fee schedule instead, which a practitioner cannot edit.
import { Button, Checkbox, Select, StatusChip } from '@scripsteam/scrips-signal-ds';
import { formatMoney } from '@/shared/lib/money';
import type { InvoiceOrderServiceCandidate } from '@/shared/types/billing';
import {
  codeLabel,
  DISMISS_TOOLTIP,
  groupFee,
  groupReadiness,
  lineFee,
  orderStatusLabel,
  orderedByLabel,
  type OrderGroup,
  type ServiceChoices,
} from '@/billing/lib/pending-orders';
import { V2_EYEBROW } from './lib/design-v2';

/** The wrap-up invoice panel's own cell recipe (DS `WrapUpInvoicePanel` TD_BASE), as named DS classes. */
export const PO_TD =
  'align-top pt-3 pr-2 pb-3 border-b border-border-default text-size-sm text-text-primary';
export const PO_TH =
  'text-left pb-3 pr-2 text-size-eyebrow font-bold uppercase tracking-normal text-brand-depth whitespace-nowrap';
/** Design sheet v1.1 §1 (switch ON only): column labels are the DS caps label in text-secondary
 *  (H11) — labels whisper, brand stays on actions. */
export const PO_TH_V2 = `text-left pb-3 pr-2 ${V2_EYEBROW} whitespace-nowrap`;
const EM_DASH = '—';

function candidateLabel(c: InvoiceOrderServiceCandidate): string {
  return `${c.description} · ${c.code} · ${formatMoney(c.fee)}`;
}

export interface WrapUpPendingOrderRowProps {
  group: OrderGroup;
  choices: ServiceChoices;
  /** `canSeeInvoice && invoiceId && orders.canEdit`: checkbox, picker and row actions. */
  editable: boolean;
  busy: boolean;
  selected: boolean;
  onToggle: (key: string, selected: boolean) => void;
  onChoose: (sourceOrderKey: string, serviceId: string) => void;
  onAdd: (group: OrderGroup) => void;
  /** NoPrice: open the add-service picker for this order. */
  onPickService: (group: OrderGroup) => void;
  onDismiss: (group: OrderGroup) => void;
}

export function WrapUpPendingOrderRow({
  group,
  choices,
  editable,
  busy,
  selected,
  onToggle,
  onChoose,
  onAdd,
  onPickService,
  onDismiss,
}: WrapUpPendingOrderRowProps) {
  const { primary, lines } = group;
  const readiness = groupReadiness(group, choices);
  const fee = groupFee(group, choices);
  const twoLines = lines.length > 1;
  const unsigned = lines.some((l) => !l.signed);
  const ambiguous = lines.filter((l) => l.priceState === 'Ambiguous');
  const status = orderStatusLabel(primary);
  const orderedBy = orderedByLabel(primary);

  return (
    <tr data-testid="pending-order-row" data-selected={selected || undefined} className={selected ? 'bg-grey-100' : undefined}>
      {editable && (
        <td className={`${PO_TD} w-8 pl-2`}>
          <Checkbox
            aria-label={`Select ${primary.display}`}
            checked={selected}
            // PROD-2679: every pending row can be ticked (Dismiss applies to all of them); bulk Add
            // takes only the ready ones.
            disabled={busy}
            onCheckedChange={(v) => onToggle(group.key, v)}
          />
        </td>
      )}
      <td className={`${PO_TD} min-w-0`}>
        <div data-slot="po-name" className="font-bold break-words">{primary.display}</div>
        <div data-slot="po-meta" className="text-size-2xs text-text-hint tabular-nums">{codeLabel(primary)}</div>
        {twoLines && <div className="text-size-2xs text-text-hint">Adds 2 lines: product + administration</div>}
        {primary.diagnoses.length > 0 && (
          <div className="text-size-2xs text-text-hint break-words">
            Dx: {primary.diagnoses.map((d) => [d.code, d.display].filter(Boolean).join(' ')).join('; ')}
          </div>
        )}
        {(unsigned || readiness === 'noPrice') && (
          <div className="mt-1 flex flex-wrap gap-2">
            {unsigned && (
              <StatusChip semantic="warning" tone="soft" size="xs">
                Not signed
              </StatusChip>
            )}
            {readiness === 'noPrice' && (
              <StatusChip semantic="warning" tone="soft" size="xs">
                No price in fee schedule
              </StatusChip>
            )}
          </div>
        )}
      </td>
      <td className={`${PO_TD} text-text-secondary`}>{primary.orderType}</td>
      {/* PROD-2630: the chip is neutral grey for every status on purpose — it is information about
          the clinical order and nothing on the invoice depends on it. */}
      <td className={PO_TD} data-testid="order-status-cell">
        {status ? (
          <StatusChip semantic="neutral" tone="soft" size="xs">
            {status}
          </StatusChip>
        ) : (
          <span className="text-text-hint">{EM_DASH}</span>
        )}
      </td>
      <td className={`${PO_TD} text-text-secondary`} data-testid="ordered-by-cell">
        {orderedBy ?? <span className="text-text-hint">{EM_DASH}</span>}
      </td>
      <td className={`${PO_TD} text-right tabular-nums whitespace-nowrap`}>
        {ambiguous.length > 0 && editable ? (
          <div className="flex flex-col items-end gap-1">
            {ambiguous.map((line) => (
              <Select
                key={line.sourceOrderKey}
                size="compact"
                aria-label={`Choose service for ${line.display}`}
                placeholder="Choose service"
                options={line.serviceCandidates.map((c) => ({ label: candidateLabel(c), value: c.id }))}
                value={choices[line.sourceOrderKey]}
                onChange={(v) => onChoose(line.sourceOrderKey, v)}
                disabled={busy}
              />
            ))}
            {fee !== null && <span className="text-text-secondary">{formatMoney(fee)}</span>}
          </div>
        ) : fee !== null ? (
          <>
            <span className="text-text-secondary">{formatMoney(fee)}</span>
            {twoLines && (
              <div className="text-size-2xs text-text-hint">
                {lines
                  .map((l) => {
                    const f = formatMoney(lineFee(l, choices));
                    return l.chargeRole === 'VaccineAdministration' ? `${f} admin (${l.serviceCode ?? '90471'})` : f;
                  })
                  .join(' + ')}
              </div>
            )}
          </>
        ) : (
          <span className="text-text-hint">
            {readiness === 'needsChoice' ? 'Choose service' : readiness === 'noPrice' ? 'No price' : EM_DASH}
          </span>
        )}
      </td>
      {editable && (
        <td className={`${PO_TD} pr-0`}>
          <div className="flex items-start justify-end gap-1">
            <Button variant="tertiary" size="sm" title={DISMISS_TOOLTIP} disabled={busy} onClick={() => onDismiss(group)}>
              Dismiss
            </Button>
            {readiness === 'noPrice' ? (
              <Button variant="secondary" size="sm" disabled={busy} onClick={() => onPickService(group)}>
                Choose service
              </Button>
            ) : (
              <Button variant="secondary" size="sm" disabled={busy || readiness !== 'ready'} onClick={() => onAdd(group)}>
                Add to invoice
              </Button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}
